/**
 * HVA DU KAN GJØRE MED DENNE RADEN (grep D1, QA13 eksempel 22 og punkt 110).
 *
 * Nettleseren spør den samme `can()` som API-et, men aktøren den bygger fra `/v1/me` har tomme
 * årgangsroller, festivaltilganger og delinger, og vet ikke om en programpost er husets egen eller
 * delt. Den kan derfor bare svare «kan rollen min dette et sted?». Det er grunnen til at AnnM fikk
 * åpnet «Rediger kontakt» på Atle og ble stoppet først ved Lagre.
 *
 * `viewer` er det samme svaret regnet på serveren, av den samme `can()`, med den fulle aktøren og
 * den konkrete raden. Det er ikke en fjerde koding av rettighetene (vedtak C-29): ingen regel står
 * her, bare utfallet av den ene funksjonen.
 *
 * `lockedFields` er feltene du ikke får skrive selv om du får skrive raden, med grunnen. Grunnen er
 * en kode; nettleseren gjør den til en setning (`viewer.locked.<reason>`), og `by` er navnet på den
 * som eier feltet når det finnes («byrået Kapabel»).
 */
import { z } from 'zod';

/**
 * Hvorfor et felt er låst. Lukket liste: en ny grunn er en ny setning i begge språkfilene.
 *
 * - `owned_by_person`: personen fører feltet selv (godtatt kobling, eller innlogging).
 * - `login_email`: innloggingsadressen, som bare personen kan bytte.
 * - `owned_by_other_organization`: byrået som booker personen eier navnet og kontaktinfoen.
 * - `organiser_only`: bare arrangøren (huset programposten hører til) kan sette dette (110).
 * - `no_permission`: rollen din mangler cella for akkurat dette feltet.
 */
export const LOCKED_FIELD_REASONS = [
  'owned_by_person',
  'login_email',
  'owned_by_other_organization',
  'organiser_only',
  'no_permission',
] as const;
export const lockedFieldReasonSchema = z.enum(LOCKED_FIELD_REASONS);

export const lockedFieldSchema = z
  .object({
    /** Feltnavnet slik det står i forespørselen som ville skrevet det (`displayName`, `phone`). */
    field: z.string(),
    reason: lockedFieldReasonSchema,
    /** Hvem som eier feltet, når det er noen andre enn personen selv. */
    by: z.string().nullable().optional(),
  })
  .meta({ id: 'LockedField' });

export const rowViewerSchema = z
  .object({
    canEdit: z.boolean(),
    canDelete: z.boolean(),
    canShare: z.boolean(),
    /** Utelatt når ingen felt er låst. */
    lockedFields: z.array(lockedFieldSchema).optional(),
  })
  .meta({ id: 'RowViewer' });

export type LockedFieldReason = z.infer<typeof lockedFieldReasonSchema>;
export type LockedField = z.infer<typeof lockedFieldSchema>;
export type RowViewer = z.infer<typeof rowViewerSchema>;

/** Svaret for en rad ingen kan gjøre noe med — en visning. */
export const READ_ONLY_VIEWER: RowViewer = Object.freeze({
  canEdit: false,
  canDelete: false,
  canShare: false,
});

/** Grunnen feltet er låst, eller null. Bare feltlåsen: uten `canEdit` er hele raden låst. */
export function lockedField(
  viewer: RowViewer | null | undefined,
  field: string,
): LockedField | null {
  return viewer?.lockedFields?.find((one) => one.field === field) ?? null;
}
